/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import LearnerShell from "./learnerShell";
import { learnerCertificatesApi } from "./_learnerActions";



const listOf = (data) =>
  Array.isArray(data) ? data : data?.list || data?.certificates || [];

const fmtDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt) ? null : dt.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

// Simplified to only fetch Course Title
const getCourseTitle = (cert, t) => {
  return cert?.course?.title || cert?.courseTitle || t("learner.lms.course.untitled", "Course");
};

const matchesSearch = (cert, q, t) => {
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return true;
  const courseTitle = getCourseTitle(cert, t);

  return [
    courseTitle,
    cert?.certificateNumber,
    cert?.overallScore,
    fmtDate(cert?.issuedAt || cert?.certificateIssuedAt || cert?.completedAt),
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};

const CertificateCard = ({ cert, t, onView }) => {
  const title = getCourseTitle(cert, t);
  const issued = fmtDate(cert?.issuedAt || cert?.certificateIssuedAt || cert?.completedAt);
  const expires = fmtDate(cert?.expiresAt || cert?.certificateExpiresAt);
  const expired = !!cert?.expired;
  const previewUrl = cert?.certificatePreviewUrl || cert?.certificateUrl || cert?.url;
  const pdfDownloadUrl = cert?.certificateDownloadUrl || cert?.certificateUrl || cert?.url;

  return (
    <article className="uniq-cert-card">
      <div className={`uniq-cert-card-topbar ${expired ? 'expired' : 'course'}`}></div>

      <div className="uniq-cert-card-body">
        {expired && (
          <div className="uniq-cert-card-badges">
            <span className="uniq-cert-badge expired">
              <i className="fas fa-exclamation-circle"></i>
              {t("learner.lms.cert.expired", "Expired")}
            </span>
          </div>
        )}

        <h3 className="uniq-cert-card-title">{title}</h3>

        <div className="uniq-cert-card-meta">
          {cert?.certificateNumber && (
            <div style={{ color: '#555', fontSize: '13px', margin: '8px 0' }}>
              Certificate Number : {cert.certificateNumber}
            </div>
          )}

          <div className="uniq-cert-meta-grid">
            {issued && (
              <div className="uniq-cert-meta-item">
                <span>{t("learner.lms.cert.issued", "Issued")}</span>
                <span>{issued}</span>
              </div>
            )}
            {expires && (
              <div className={`uniq-cert-meta-item ${expired ? 'expired' : ''}`} style={{ gridColumn: '1 / -1' }}>
                <span>{expired ? t("learner.lms.cert.expired", "Expired") : t("learner.lms.cert.expires", "Expires")}</span>
                <span>{expires}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="lms-course-card-actions" style={{ padding: '20px', borderTop: '1px solid #eee' }}>
        {pdfDownloadUrl && (
          <a href={pdfDownloadUrl} download={`${cert?.certificateNumber || "certificate"}.pdf`} className="btn lms-btn-primary">
            {t("learner.lms.cert.downloadPdf", "Download")}
          </a>
        )}
        {previewUrl && (
          <button onClick={() => onView(previewUrl, title)} className="btn lms-btn-ghost">
            {t("common.view", "View")}
          </button>
        )}
      </div>
    </article>
  );
};

const LearnerCertificates = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [previewModalUrl, setPreviewModalUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await learnerCertificatesApi();
        if (res?.data?.status === 200) {
          setRows(listOf(res?.data?.data));
        } else {
          setError(res?.data?.message || t("common.somethingWentWrong"));
        }
      } catch (e) {
        setError(e?.response?.data?.message || t("common.somethingWentWrong"));
      }
      setLoading(false);
    })();
  }, []);

  const filteredCertificates = useMemo(() => {
    // 1. Filter out modules directly (if API still returns them)
    const coursesOnly = rows.filter((cert) =>
      String(cert?.certificateType || cert?.type || "course").toLowerCase() !== "module"
    );
    // 2. Apply search query
    return coursesOnly.filter((cert) => matchesSearch(cert, query, t));
  }, [rows, query, t]);

  return (
    <LearnerShell title={t("learner.lms.nav.certificates", "My Certificates")} wide>
      <div className="uniq-cert-wrapper">
        <div className="uniq-cert-container">

          <div className="uniq-cert-header-section">
            <div>
              <h1 className="uniq-cert-page-title">
                {t("learner.lms.nav.certificates", "My Certificates")}
              </h1>
              <p className="uniq-cert-page-subtitle">
                {t("learner.lms.cert.organized", "View and download all your earned course certificates.")}
              </p>
            </div>
          </div>

          {loading && (
            <div className="uniq-cert-loading">
              <i className="fas fa-circle-notch fa-spin fa-2x"></i>
              <p>{t("common.loading", "Loading certificates...")}</p>
            </div>
          )}

          {!loading && error && (
            <div className="uniq-cert-error">
              <i className="fas fa-exclamation-triangle fa-2x"></i>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {rows.length > 0 ? (
                <>
                  <div className="uniq-cert-controls" style={{ justifyContent: 'flex-start' }}>
                    <div className="uniq-cert-search-box">
                      <i className="fas fa-search"></i>
                      <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("learner.lms.cert.searchPlaceholder", "Search by course name, ID, or date...")}
                      />
                    </div>
                  </div>

                  {filteredCertificates.length > 0 ? (
                    <div className="uniq-cert-grid">
                      {filteredCertificates.map((c, i) => (
                        <CertificateCard 
                          cert={c} 
                          t={t} 
                          key={c?._id || c?.certificateNumber || i} 
                          onView={(url, title) => {
                            setPreviewModalUrl(url);
                            setPreviewTitle(title);
                          }} 
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="uniq-cert-empty-state">
                      <i className="fas fa-search"></i>
                      <h3>No results found</h3>
                      <p>{t("learner.lms.cert.noResults", "No certificates match your current search query.")}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="uniq-cert-empty-state">
                  <i className="fas fa-award"></i>
                  <h3>No certificates yet</h3>
                  <p>{t("learner.lms.cert.none", "Complete a course to earn your first certificate.")}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        open={!!previewModalUrl}
        onClose={() => setPreviewModalUrl(null)}
        center
        showCloseIcon={false}
        styles={{
          modal: { maxWidth: '850px', width: '90%', height: '70vh', maxHeight: '550px', padding: '20px', borderRadius: '8px' }
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
            {previewTitle || t("learner.lms.cert.courseCertificate", "Course Certificate")}
          </h3>
          <button 
            onClick={() => setPreviewModalUrl(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
            title={t("common.close", "Close")}
          >
            <i className="fas fa-times" style={{ fontSize: '1.5rem', color: '#333' }}></i>
          </button>
        </div>
        <iframe
          src={previewModalUrl ? `${previewModalUrl}#toolbar=0&navpanes=0&scrollbar=0&view=Fit` : ""}
          style={{ width: '100%', height: 'calc(100% - 45px)', border: '1px solid #eee', borderRadius: '4px', overflow: 'hidden' }}
          title="Certificate Preview"
        />
      </Modal>
    </LearnerShell>
  );
};

export default LearnerCertificates;
